import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, ExternalLink, IndianRupee, Loader2, Lock, MessageCircle, Plus, Save, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import CountryPhoneInput from "./CountryPhoneInput";
import LeadAutofill from "./LeadAutofill";
import { isValidPhoneNumber } from "../../lib/phone";
import { fmtInr, linkUrl, waHref, copyUrl } from "./paymentLinkUtils";

const lbl = "text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block";
const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#1E3A8A]";

function TextField({ label, value, onChange, placeholder, textarea, testId }) {
    const El = textarea ? "textarea" : "input";
    return (
        <div>
            <label className={lbl}>{label}</label>
            <El value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={textarea ? 2 : undefined}
                className={inputCls} data-testid={testId} />
        </div>
    );
}

export function AdminOnlyBadge() {
    return (
        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200" data-testid="admin-only-badge">
            <Lock className="w-2.5 h-2.5" /> Admin only
        </span>
    );
}

function initialForm(editing, prefill, defaultAcctId) {
    const src = editing || prefill || {};
    return {
        amount: editing ? String(editing.amount) : "",
        session_count: editing?.session_count ? String(editing.session_count) : "",
        client_search_name: src.client_search_name || "",
        student_name: src.student_name || "",
        parent_name: src.parent_name || "",
        enquiry_id: src.enquiry_id || "",
        description: editing?.description || "",
        payment_account_id: editing?.payment_account_id || defaultAcctId || "",
        contact_number: editing ? (editing.contact_number ? `+${editing.contact_number}` : "") : (prefill?.contact_number || ""),
        parent_link_id: prefill?.parent_link_id || "",
    };
}

function SavedView({ link, editing, onClose }) {
    return (
        <div className="p-5 space-y-4" data-testid="payment-link-generated">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                {editing ? "Payment link updated — the same link now shows " : "Payment link created for "}
                <strong>{fmtInr(link.amount)}</strong>{link.session_count ? ` · ${link.session_count} sessions` : ""}.
            </div>
            <div className="flex gap-2">
                <input readOnly value={linkUrl(link)} className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono bg-slate-50" data-testid="payment-link-generated-url" />
                <button type="button" onClick={() => copyUrl(linkUrl(link), toast)} className="px-3 py-2 bg-[#1E3A8A] text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1" data-testid="payment-link-generated-copy">
                    <Copy className="w-4 h-4" /> Copy
                </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <a href={waHref(link)} target="_blank" rel="noopener noreferrer" data-testid="payment-link-generated-whatsapp"
                    className="inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-3 py-2 rounded-lg text-sm">
                    <MessageCircle className="w-4 h-4" /> {link.contact_number ? `Send to +${link.contact_number}` : "Send on WhatsApp"}
                </a>
                <a href={linkUrl(link)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-[#1E3A8A] text-slate-700 font-semibold px-3 py-2 rounded-lg text-sm">
                    <ExternalLink className="w-4 h-4" /> Preview
                </a>
            </div>
            {link.contact_number && (
                <a href={waHref(link, true)} target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-slate-500 hover:text-slate-700 underline" data-testid="payment-link-generated-whatsapp-pick">
                    Send to a different WhatsApp contact
                </a>
            )}
            <button onClick={onClose} className="w-full text-slate-500 hover:text-slate-700 text-sm py-2">Close</button>
        </div>
    );
}

/** Create (optionally pre-filled for an existing client) or edit a pending payment link. */
export default function PaymentLinkFormModal({ accounts, editing = null, prefill = null, onClose, onSaved }) {
    const defaultAcct = accounts.find((a) => a.is_default) || accounts[0];
    const [form, setForm] = useState(() => initialForm(editing, prefill, defaultAcct?.id));
    const [contactValid, setContactValid] = useState(true);
    const [ack, setAck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [saved, setSaved] = useState(null);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const [phoneKey, setPhoneKey] = useState(0);

    // V183 — Fill only empty fields; never touches amount / sessions / description.
    const fillFromLead = (m) => {
        const patch = {};
        ["student_name", "parent_name", "client_search_name"].forEach((k) => { if (!form[k].trim() && m[k]) patch[k] = m[k]; });
        if (!form.contact_number && m.contact_number) { patch.contact_number = `+${m.contact_number}`; setContactValid(true); setPhoneKey((n) => n + 1); }
        if (m.enquiry_id && m.enquiry_id !== form.enquiry_id) patch.enquiry_id = m.enquiry_id;
        set(patch);
        toast.success(Object.keys(patch).length ? "Client details filled — please review" : "Nothing to fill — fields already have values");
    };

    const amountChanged = editing && parseFloat(form.amount) !== Number(editing.amount);
    const parentClaimed = editing && (editing.parent_confirmed_at || editing.has_screenshot);
    const needsAck = amountChanged && parentClaimed;
    // Accounts list only holds active ones; keep an inactive current account selectable when editing.
    const acctOptions = editing && !accounts.some((a) => a.id === editing.payment_account_id)
        ? [{ id: editing.payment_account_id, label: `${editing.payment_account_label || "Current account"} (inactive)` }, ...accounts]
        : accounts;

    const submit = async (e) => {
        e.preventDefault();
        const amt = parseFloat(form.amount);
        if (!(amt > 0)) { toast.error("Enter a valid amount."); return; }
        const sessions = form.session_count === "" ? null : parseInt(form.session_count, 10);
        if (sessions !== null && !(sessions >= 1 && sessions <= 1000)) { toast.error("Sessions must be between 1 and 1000, or blank."); return; }
        if (form.contact_number && !(contactValid && isValidPhoneNumber(form.contact_number))) {
            toast.error("Enter a valid contact number for the selected country, or leave it blank.");
            return;
        }
        if (needsAck && !ack) { toast.error("Tick the confirmation box to change the amount."); return; }
        const body = {
            ...form, amount: amt,
            session_count: editing ? (sessions ?? 0) : sessions,
            contact_number: (form.contact_number || "").replace(/\D/g, ""),
        };
        setSubmitting(true);
        try {
            let res;
            if (editing) {
                delete body.parent_link_id;
                res = await api.patch(`/admin/payment/links/${editing.id}`, { ...body, acknowledge_parent_confirmed: ack });
                toast.success(res.data.changed?.length ? "Payment link updated" : "No changes to save");
            } else {
                res = await api.post("/admin/payment/links", body);
            }
            setSaved(res.data.link);
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const title = editing ? "Edit Payment Link" : prefill?.parent_link_id ? "New Linked Payment Request" : "Create Payment Link";
    return (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
                data-testid={editing ? "payment-link-edit-modal" : "payment-link-create-modal"}>
                <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
                    <h3 className="font-heading font-bold text-lg text-[#0F172A]">{saved ? (editing ? "Link Updated" : "Link Ready") : title}</h3>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Close" data-testid="payment-link-form-close"><X className="w-5 h-5" /></button>
                </div>
                {saved ? <SavedView link={saved} editing={!!editing} onClose={onClose} /> : (
                    <form onSubmit={submit} className="p-5 space-y-4">
                        {editing && <p className="text-[11px] text-slate-500 font-mono">{editing.id} · same link, updated in place</p>}
                        {prefill?.parent_link_id && <p className="text-[11px] text-slate-500">Linked to original request <span className="font-mono">{prefill.parent_link_id}</span>. Client details pre-filled.</p>}
                        <div className="grid grid-cols-[1fr_auto] gap-3">
                            <div>
                                <label className={lbl}>Amount (₹) · required</label>
                                <div className="relative">
                                    <IndianRupee className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                    <input type="number" step="0.01" min="1" required autoFocus inputMode="decimal" value={form.amount}
                                        onChange={(e) => set({ amount: e.target.value })} placeholder="8400"
                                        className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg text-lg font-bold tabular-nums focus:outline-none focus:border-[#1E3A8A]"
                                        data-testid="payment-link-create-amount" />
                                </div>
                            </div>
                            <div className="w-28">
                                <label className={lbl}>Sessions</label>
                                <input type="number" min="1" max="1000" inputMode="numeric" value={form.session_count}
                                    onChange={(e) => set({ session_count: e.target.value })} placeholder="e.g. 10"
                                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-lg font-bold tabular-nums focus:outline-none focus:border-[#1E3A8A]"
                                    data-testid="payment-link-create-sessions" />
                            </div>
                        </div>
                        {needsAck && (
                            <label className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="payment-link-edit-ack-wrap">
                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                <span className="flex-1">The parent has already {editing.has_screenshot ? "uploaded a payment screenshot" : "tapped “I have paid”"} for {fmtInr(editing.amount)}. Check your bank / Razorpay first.
                                    <span className="mt-1.5 flex items-center gap-2 font-semibold">
                                        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="payment-link-edit-ack" />
                                        I understand and want to change the amount
                                    </span>
                                </span>
                            </label>
                        )}
                        <div>
                            <label className={`${lbl} flex items-center gap-2`}>Client Name / Search Name <AdminOnlyBadge /></label>
                            <input value={form.client_search_name} onChange={(e) => set({ client_search_name: e.target.value })} placeholder="e.g. Ahmed — never shown to the parent"
                                className={`${inputCls} bg-violet-50/40`} data-testid="payment-link-create-client-name" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <TextField label="Student Name (optional)" value={form.student_name} onChange={(v) => set({ student_name: v })} testId="payment-link-create-student" />
                            <TextField label="Parent Name (optional)" value={form.parent_name} onChange={(v) => set({ parent_name: v })} testId="payment-link-create-parent" />
                        </div>
                        <div data-testid="payment-link-create-contact-wrap">
                            <label className={lbl}>Contact Number <span className="font-normal text-slate-400 normal-case tracking-normal">· optional, for direct WhatsApp send</span></label>
                            <CountryPhoneInput key={phoneKey} value={form.contact_number} defaultCountry="IN" testid="payment-link-create-contact"
                                onChange={(v, meta) => { set({ contact_number: v || "" }); setContactValid(meta.valid); }} />
                        </div>
                        <div>
                            <TextField label="Enquiry / Student ID (optional)" value={form.enquiry_id} onChange={(v) => set({ enquiry_id: v })} testId="payment-link-create-enquiry" />
                            {!editing && <LeadAutofill refValue={form.enquiry_id} onFill={fillFromLead} />}
                        </div>
                        <TextField label="Payment Description (optional)" value={form.description} onChange={(v) => set({ description: v })} placeholder="10 Sessions – Mathematics Tuition" textarea testId="payment-link-create-desc" />
                        <div>
                            <label className={lbl}>Payment Account</label>
                            <select value={form.payment_account_id} onChange={(e) => set({ payment_account_id: e.target.value })}
                                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:border-[#1E3A8A]" data-testid="payment-link-create-account">
                                {acctOptions.map((a) => <option key={a.id} value={a.id}>{a.label} {a.is_default ? "· default" : ""}</option>)}
                            </select>
                        </div>
                        <div className="pt-2 flex gap-2">
                            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 font-semibold rounded-lg text-sm">Cancel</button>
                            <button type="submit" disabled={submitting} data-testid="payment-link-create-submit"
                                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold rounded-lg text-sm disabled:opacity-60">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                {editing ? "Save Changes" : "Generate Link"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
